/**
 * Email Templates Page - STORY-027
 *
 * CRUD interface for email templates with:
 * - Template types: relance_devis, relance_facture, bienvenue, notification
 * - Dynamic variables support
 * - HTML editor with preview
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Copy,
  Check,
  X,
  Search,
  Mail,
  AlertCircle,
  Code,
} from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  useEmailTemplates,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
} from '@/hooks/useEmails'
import type { EmailTemplate, EmailTemplateType, EmailTemplateFormData } from '@/types/email'
import { DEFAULT_TEMPLATE_VARIABLES } from '@/types/email'

// Template type labels
const templateTypeLabels: Record<EmailTemplateType, string> = {
  relance_devis: 'Relance Devis',
  relance_facture: 'Relance Facture',
  bienvenue: 'Bienvenue',
  notification: 'Notification',
  custom: 'Personnalise',
}

// Template type colors
const templateTypeColors: Record<EmailTemplateType, string> = {
  relance_devis: 'bg-amber-100 text-amber-800',
  relance_facture: 'bg-red-100 text-red-800',
  bienvenue: 'bg-green-100 text-green-800',
  notification: 'bg-blue-100 text-blue-800',
  custom: 'bg-purple-100 text-purple-800',
}

// Default templates
const defaultTemplates: Partial<EmailTemplateFormData>[] = [
  {
    name: 'Relance Devis Standard',
    type: 'relance_devis',
    subject: 'Rappel: Votre devis {{devis_numero}} expire bientot',
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #10b981; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin-top: 20px; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{entreprise_nom}}</h1>
    </div>
    <div class="content">
      <p>Bonjour {{client_name}},</p>
      <p>Nous vous rappelons que votre devis <strong>{{devis_numero}}</strong> d'un montant de <strong>{{montant_ttc}}</strong> expire le <strong>{{date_validite}}</strong>.</p>
      <p>N'hesitez pas a nous contacter si vous avez des questions ou si vous souhaitez proceder a la validation.</p>
      <p><a href="{{lien_devis}}" class="button">Voir le devis</a></p>
      <p>Cordialement,<br>L'equipe {{entreprise_nom}}</p>
    </div>
    <div class="footer">
      <p>Cet email a ete envoye automatiquement. Merci de ne pas y repondre directement.</p>
    </div>
  </div>
</body>
</html>
    `.trim(),
    variables: DEFAULT_TEMPLATE_VARIABLES.relance_devis,
  },
  {
    name: 'Relance Facture Standard',
    type: 'relance_facture',
    subject: 'Rappel: Facture {{facture_numero}} en attente de paiement',
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #ef4444; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
    .alert { background: #fef2f2; border-left: 4px solid #ef4444; padding: 15px; margin: 20px 0; }
    .button { display: inline-block; background: #ef4444; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin-top: 20px; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Rappel de paiement</h1>
    </div>
    <div class="content">
      <p>Bonjour {{client_name}},</p>
      <div class="alert">
        <strong>Facture:</strong> {{facture_numero}}<br>
        <strong>Montant:</strong> {{montant_ttc}}<br>
        <strong>Echeance:</strong> {{date_echeance}}<br>
        <strong>Retard:</strong> {{jours_retard}} jours
      </div>
      <p>Nous n'avons pas encore recu le paiement de cette facture. Si vous avez deja effectue le reglement, veuillez ignorer ce message.</p>
      <p><a href="{{lien_facture}}" class="button">Voir la facture</a></p>
      <p>Cordialement,<br>L'equipe {{entreprise_nom}}</p>
    </div>
    <div class="footer">
      <p>Cet email a ete envoye automatiquement. Merci de ne pas y repondre directement.</p>
    </div>
  </div>
</body>
</html>
    `.trim(),
    variables: DEFAULT_TEMPLATE_VARIABLES.relance_facture,
  },
  {
    name: 'Email de Bienvenue',
    type: 'bienvenue',
    subject: 'Bienvenue chez {{entreprise_nom}} !',
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #10b981; color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .header h1 { margin: 0; font-size: 28px; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin-top: 20px; }
    .info-box { background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin: 20px 0; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Bienvenue !</h1>
    </div>
    <div class="content">
      <p>Bonjour {{client_name}},</p>
      <p>Nous sommes ravis de vous accueillir chez <strong>{{entreprise_nom}}</strong> !</p>
      <div class="info-box">
        <h3>Vos informations de contact</h3>
        <p>
          <strong>Email:</strong> {{contact_email}}<br>
          <strong>Telephone:</strong> {{contact_telephone}}
        </p>
      </div>
      <p>Vous pouvez acceder a votre espace client a tout moment pour consulter vos devis et factures.</p>
      <p><a href="{{lien_espace_client}}" class="button">Acceder a mon espace</a></p>
      <p>A bientot !<br>L'equipe {{entreprise_nom}}</p>
    </div>
    <div class="footer">
      <p>Cet email a ete envoye automatiquement. Merci de ne pas y repondre directement.</p>
    </div>
  </div>
</body>
</html>
    `.trim(),
    variables: DEFAULT_TEMPLATE_VARIABLES.bienvenue,
  },
  {
    name: 'Notification Generale',
    type: 'notification',
    subject: '{{notification_titre}}',
    html_content: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #3b82f6; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #3b82f6; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin-top: 20px; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{notification_titre}}</h1>
    </div>
    <div class="content">
      <p>Bonjour {{client_name}},</p>
      <p>{{notification_message}}</p>
      <p><a href="{{lien_action}}" class="button">En savoir plus</a></p>
      <p>Cordialement,<br>L'equipe {{entreprise_nom}}</p>
    </div>
    <div class="footer">
      <p>Cet email a ete envoye automatiquement. Merci de ne pas y repondre directement.</p>
    </div>
  </div>
</body>
</html>
    `.trim(),
    variables: DEFAULT_TEMPLATE_VARIABLES.notification,
  },
]

export default function EmailTemplatesPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<EmailTemplateType | 'all'>('all')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<Partial<EmailTemplateFormData>>({
    name: '',
    type: 'custom',
    subject: '',
    html_content: '',
    text_content: '',
    variables: [],
    is_active: true,
  })

  // Queries and mutations
  const { data: templates, isLoading, error } = useEmailTemplates()
  const createMutation = useCreateEmailTemplate()
  const updateMutation = useUpdateEmailTemplate()
  const deleteMutation = useDeleteEmailTemplate()

  // Filter templates
  const filteredTemplates = templates?.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = selectedType === 'all' || template.type === selectedType
    return matchesSearch && matchesType
  })

  // Handlers
  const handleCreateTemplate = async () => {
    try {
      await createMutation.mutateAsync(formData as EmailTemplateFormData)
      setIsCreateModalOpen(false)
      resetForm()
    } catch (error) {
      console.error('Failed to create template:', error)
    }
  }

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return
    try {
      await updateMutation.mutateAsync({
        id: selectedTemplate.id,
        data: formData,
      })
      setIsEditModalOpen(false)
      resetForm()
    } catch (error) {
      console.error('Failed to update template:', error)
    }
  }

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return
    try {
      await deleteMutation.mutateAsync(selectedTemplate.id)
      setIsDeleteModalOpen(false)
      setSelectedTemplate(null)
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }

  const openEditModal = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      html_content: template.html_content,
      text_content: template.text_content || '',
      variables: template.variables,
      is_active: template.is_active,
    })
    setIsEditModalOpen(true)
  }

  const openPreviewModal = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setIsPreviewModalOpen(true)
  }

  const openDeleteModal = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setIsDeleteModalOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'custom',
      subject: '',
      html_content: '',
      text_content: '',
      variables: [],
      is_active: true,
    })
    setSelectedTemplate(null)
  }

  const loadDefaultTemplate = (index: number) => {
    const defaultTemplate = defaultTemplates[index]
    if (defaultTemplate) {
      setFormData({
        ...formData,
        ...defaultTemplate,
      })
    }
  }

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable)
    setCopiedVariable(variable)
    setTimeout(() => setCopiedVariable(null), 2000)
  }

  const updateFormType = (type: EmailTemplateType) => {
    setFormData({
      ...formData,
      type,
      variables: DEFAULT_TEMPLATE_VARIABLES[type] || [],
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-brown-600">Erreur lors du chargement des templates</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Templates Email
          </h1>
          <p className="text-brown-600 mt-1">
            Gerez vos modeles d'emails avec variables dynamiques
          </p>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Nouveau template
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher un template..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedType === 'all' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setSelectedType('all')}
            >
              Tous
            </Button>
            {Object.entries(templateTypeLabels).map(([type, label]) => (
              <Button
                key={type}
                variant={selectedType === type ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setSelectedType(type as EmailTemplateType)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Templates Grid */}
      {filteredTemplates && filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card className="h-full flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-mint-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-brown-900">
                          {template.name}
                        </h3>
                        <Badge className={templateTypeColors[template.type]}>
                          {templateTypeLabels[template.type]}
                        </Badge>
                      </div>
                    </div>
                    {!template.is_active && (
                      <Badge variant="secondary">Inactif</Badge>
                    )}
                  </div>

                  <p className="text-sm text-brown-600 mb-4 line-clamp-2">
                    <span className="font-medium">Sujet:</span> {template.subject}
                  </p>

                  {template.variables && template.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {template.variables.slice(0, 3).map((variable) => (
                        <span
                          key={variable}
                          className="text-xs bg-beige-100 text-brown-600 px-2 py-1 rounded"
                        >
                          {variable}
                        </span>
                      ))}
                      {template.variables.length > 3 && (
                        <span className="text-xs text-brown-500">
                          +{template.variables.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mt-auto flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreviewModal(template)}
                      leftIcon={<Eye className="w-4 h-4" />}
                    >
                      Apercu
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(template)}
                      leftIcon={<Edit2 className="w-4 h-4" />}
                    >
                      Modifier
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteModal(template)}
                      leftIcon={<Trash2 className="w-4 h-4" />}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Supprimer
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <EmptyState
          icon={<FileText className="w-12 h-12" />}
          title="Aucun template"
          description="Creez votre premier template email pour commencer"
          action={
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Creer un template
            </Button>
          }
        />
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setIsEditModalOpen(false)
          resetForm()
        }}
        title={isEditModalOpen ? 'Modifier le template' : 'Nouveau template'}
        size="xl"
      >
        <div className="space-y-6">
          {/* Default templates */}
          {!isEditModalOpen && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-brown-700 mb-2">
                Templates predefinies
              </label>
              <div className="flex flex-wrap gap-2">
                {defaultTemplates.map((dt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => loadDefaultTemplate(index)}
                  >
                    {dt.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom du template"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Relance devis standard"
            />

            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => updateFormType(e.target.value as EmailTemplateType)}
                className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              >
                {Object.entries(templateTypeLabels).map(([type, label]) => (
                  <option key={type} value={type}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Input
            label="Sujet de l'email"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Ex: Rappel: Votre devis {{devis_numero}} expire bientot"
          />

          {/* Variables */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Variables disponibles
            </label>
            <div className="flex flex-wrap gap-2 p-3 bg-beige-50 rounded-lg">
              {(formData.variables || DEFAULT_TEMPLATE_VARIABLES[formData.type as EmailTemplateType] || []).map(
                (variable) => (
                  <button
                    key={variable}
                    onClick={() => copyVariable(variable)}
                    className="inline-flex items-center gap-1 text-xs bg-white border border-beige-200 text-brown-600 px-2 py-1 rounded hover:bg-beige-100 transition-colors"
                  >
                    <Code className="w-3 h-3" />
                    {variable}
                    {copiedVariable === variable ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                )
              )}
            </div>
          </div>

          {/* HTML Content */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Contenu HTML
            </label>
            <textarea
              value={formData.html_content}
              onChange={(e) =>
                setFormData({ ...formData, html_content: e.target.value })
              }
              placeholder="<!DOCTYPE html>..."
              rows={12}
              className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent font-mono text-sm"
            />
          </div>

          {/* Text Content (optional) */}
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Contenu texte (optionnel)
            </label>
            <textarea
              value={formData.text_content || ''}
              onChange={(e) =>
                setFormData({ ...formData, text_content: e.target.value })
              }
              placeholder="Version texte de l'email..."
              rows={4}
              className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
            />
            <label htmlFor="is_active" className="text-sm text-brown-700">
              Template actif
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-beige-200">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false)
                setIsEditModalOpen(false)
                resetForm()
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={isEditModalOpen ? handleUpdateTemplate : handleCreateTemplate}
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {isEditModalOpen ? 'Enregistrer' : 'Creer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false)
          setSelectedTemplate(null)
        }}
        title={`Apercu: ${selectedTemplate?.name || ''}`}
        size="xl"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="bg-beige-50 p-4 rounded-lg">
              <p className="text-sm text-brown-600">
                <span className="font-medium">Sujet:</span> {selectedTemplate.subject}
              </p>
            </div>
            <div
              className="border border-beige-200 rounded-lg p-4 bg-white"
              dangerouslySetInnerHTML={{ __html: selectedTemplate.html_content }}
            />
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedTemplate(null)
        }}
        title="Confirmer la suppression"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir supprimer le template{' '}
            <strong>{selectedTemplate?.name}</strong> ? Cette action est irreversible.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setIsDeleteModalOpen(false)
                setSelectedTemplate(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteTemplate}
              isLoading={deleteMutation.isPending}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
